import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ChefHat,
  Boxes,
  Receipt,
  TrendingUp,
  ScaleIcon,
  Settings,
  Circle,
  RefreshCw,
  LogOut,
  Wallet,
  Monitor,
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const NAV = [
  { to: "/", label: "Visão Geral", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/conciliacao", label: "Conciliação", icon: ScaleIcon, testId: "nav-reconciliation" },
  { to: "/fichas", label: "Fichas Técnicas", icon: ChefHat, testId: "nav-recipes" },
  { to: "/estoque", label: "Estoque", icon: Boxes, testId: "nav-inventory" },
  { to: "/compras", label: "Compras", icon: Receipt, testId: "nav-purchases" },
  { to: "/contas-a-pagar", label: "Contas a Pagar", icon: Wallet, testId: "nav-contas-pagar" },
  { to: "/vendas", label: "Vendas", icon: TrendingUp, testId: "nav-sales" },
  { to: "/pos", label: "POS", icon: Monitor, testId: "nav-pos" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, testId: "nav-settings" },
];

function formatRelative(ts) {
  if (!ts) return "—";
  const d = typeof ts === "string" ? new Date(ts) : ts;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "há poucos segundos";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  return `há ${Math.floor(diff / 86400)} dias`;
}

export default function Layout() {
  const [status, setStatus] = useState({ omie_configured: false, pdv_configured: false });
  const [lastSync, setLastSync] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const onLogout = () => {
    logout();
    toast.success("Sessão encerrada");
    navigate("/login", { replace: true });
  };

  const loadStatus = () => {
    api.get("/settings").then(r => setStatus(r.data)).catch(() => {});
    api.get("/sync/last").then(r => setLastSync(r.data?.ts || null)).catch(() => {});
  };

  // Auto sync on app load (quick — today only)
  useEffect(() => {
    loadStatus();
    api.post("/sync/quick").then(r => {
      if (r.data?.ok) {
        const ins = r.data.inserted || 0;
        const upd = r.data.updated || 0;
        if (ins || upd) {
          toast.success(`Dados atualizados: ${ins} novos, ${upd} ajustados`, { duration: 3000 });
        }
        api.get("/sync/last").then(r => setLastSync(r.data?.ts || null));
      }
    }).catch(() => {});
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/sync/all");
      const sales = r.data?.pdv_sales;
      const inv = r.data?.omie_inventory;
      const parts = [];
      if (sales?.ok) parts.push(`Vendas: ${sales.cupons} cupons (+${sales.inserted} novos)`);
      if (inv?.ok) parts.push(`Estoque: ${inv.count} itens`);
      toast.success(parts.join(" · ") || "Sync concluída");
      api.get("/sync/last").then(r => setLastSync(r.data?.ts || null));
    } catch (e) {
      toast.error(`Falha: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--ifl-bg)" }} data-testid="app-shell">
      <aside
        className="w-72 shrink-0 flex flex-col px-7 py-8 border-r"
        style={{ borderColor: "var(--ifl-border)" }}
        data-testid="sidebar"
      >
        <div className="mb-12 ifl-fade-up">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: "var(--ifl-terracotta)" }} />
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: "var(--ifl-mustard)" }} />
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: "var(--ifl-success)" }} />
          </div>
          <div className="heading-caps text-sm" style={{ letterSpacing: "0.22em" }}>
            INDIE FOOD LAB
          </div>
          <div className="font-italic-serif text-xl mt-1" style={{ color: "var(--ifl-text)" }}>
            custos <span style={{ color: "var(--ifl-muted)" }}>&amp;</span> insumos.
          </div>
        </div>

        <nav className="flex flex-col gap-1 ifl-fade-up" style={{ animationDelay: "60ms" }}>
          <div className="label-caps mb-2 px-3">Operação</div>
          {NAV.map(({ to, label, icon: Icon, end, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={({ isActive }) =>
                "group flex items-center gap-3 py-2.5 px-3 rounded-xl transition-all " +
                (isActive ? "bg-white border" : "hover:bg-white/60")
              }
              style={({ isActive }) => ({
                borderColor: isActive ? "var(--ifl-border)" : "transparent",
                color: "var(--ifl-text)",
              })}
            >
              <Icon size={16} strokeWidth={1.5} />
              <span className="text-[0.72rem] tracking-[0.12em] uppercase font-display font-medium">
                {label}
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto pt-8 ifl-fade-up" style={{ animationDelay: "120ms" }}>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border mb-4"
            style={{
              borderColor: "var(--ifl-border)",
              background: refreshing ? "var(--ifl-bg)" : "#fff",
              cursor: refreshing ? "wait" : "pointer",
            }}
            data-testid="global-refresh-btn"
          >
            <span className="flex items-center gap-2">
              <RefreshCw size={14} strokeWidth={1.5} className={refreshing ? "ifl-pulse" : ""} />
              <span className="font-display text-[0.72rem] uppercase tracking-widest font-medium">
                {refreshing ? "Atualizando…" : "Atualizar"}
              </span>
            </span>
            <span className="font-body text-[0.62rem]" style={{ color: "var(--ifl-muted)" }}>
              {formatRelative(lastSync)}
            </span>
          </button>

          <div className="label-caps mb-3">Integrações</div>
          <div className="flex flex-col gap-2" data-testid="integration-status">
            <div className="flex items-center justify-between text-xs">
              <span className="font-body" style={{ color: "var(--ifl-muted)" }}>Omie ERP</span>
              <span className="flex items-center gap-2">
                <Circle
                  size={8}
                  fill={status.omie_configured ? "var(--ifl-success)" : "var(--ifl-danger)"}
                  color={status.omie_configured ? "var(--ifl-success)" : "var(--ifl-danger)"}
                />
                <span className="font-display uppercase tracking-widest" style={{ fontSize: "0.62rem" }}>
                  {status.omie_configured ? "ok" : "off"}
                </span>
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="font-body" style={{ color: "var(--ifl-muted)" }}>PDV Legal</span>
              <span className="flex items-center gap-2">
                <Circle
                  size={8}
                  fill={status.pdv_configured ? "var(--ifl-success)" : "var(--ifl-danger)"}
                  color={status.pdv_configured ? "var(--ifl-success)" : "var(--ifl-danger)"}
                />
                <span className="font-display uppercase tracking-widest" style={{ fontSize: "0.62rem" }}>
                  {status.pdv_configured ? "ok" : "off"}
                </span>
              </span>
            </div>
          </div>

          {/* Logged-in user + logout */}
          {user && (
            <div
              className="mt-6 pt-4 border-t"
              style={{ borderColor: "var(--ifl-border)" }}
              data-testid="user-block"
            >
              <div className="label-caps mb-2">Sessão</div>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-[0.72rem] uppercase tracking-widest font-medium truncate">
                    {user.name || user.email.split("@")[0]}
                  </div>
                  <div className="font-body text-[0.62rem] truncate" style={{ color: "var(--ifl-muted)" }}>
                    {user.email}
                  </div>
                </div>
                <button
                  onClick={onLogout}
                  className="shrink-0 p-2 rounded-lg border hover:bg-white transition-colors"
                  style={{ borderColor: "var(--ifl-border)", color: "var(--ifl-muted)" }}
                  title="Sair"
                  data-testid="logout-btn"
                >
                  <LogOut size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden" data-testid="main-content">
        <div className="px-8 py-8 max-w-[1480px] mx-auto" key={loc.pathname}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
